
import { defineComponent, PropType } from 'vue'

import { formatDate } from '@/utils/date'
import TEAM_PERMS from '~/utils/teamPerms'

import OwnerDetailMixin from '~/mixins/ownerDetailMixin'

import { getDefaultAvatar } from '~/utils/ui'

import { ProviderMeta } from '~/plugins/helpers/provider'
import { RepositoryCollaboratorPermission, TeamMemberRoleChoices, User } from '~/types/types'
import ZMenu from '../zeal/ZMenu/ZMenu.vue'

const REPO_PERMS = {
  ADMIN: {
    title: 'Admin',
    value: 'ADMIN',
    description:
      'Full access to the repository and settings, including the ability to manage members.'
  },
  WRITE: {
    title: 'Write',
    value: 'WRITE',
    description:
      'Full access to the repository and settings, except the ability to manage members and changing activation status.'
  },
  READ: {
    title: 'Read-only',
    value: 'READ',
    description:
      'Read-only access to the repository, without the ability to make any kind of changes.'
  }
}

export default defineComponent({
  name: 'MemberListItem',
  components: { ZMenu },
  mixins: [OwnerDetailMixin],
  props: {
    role: {
      type: String as () => RepositoryCollaboratorPermission | TeamMemberRoleChoices,
      required: true
    },
    id: {
      type: String as PropType<User['id']>,
      required: true
    },
    dateJoined: {
      type: String as PropType<User['dateJoined']>
    },
    avatar: {
      type: String as PropType<User['avatar']>
    },
    email: {
      type: String as PropType<User['email']>
    },
    fullName: {
      type: String as PropType<User['fullName']>
    },
    isRepo: {
      type: Boolean,
      default: false
    },
    isPrimaryUser: {
      type: Boolean,
      default: false
    },
    showRoleOptions: {
      type: Boolean,
      default: true
    },
    allowTransfer: {
      type: Boolean,
      default: false
    },
    isPermFromVcs: {
      type: Boolean,
      default: false
    },
    hideBorder: {
      type: Boolean,
      default: false
    },
    vcsProvider: {
      type: String
    }
  },
  computed: {
    roles(): Record<string, Record<string, string>> {
      if (this.isRepo) {
        return REPO_PERMS
      }
      return TEAM_PERMS
    },
    showOwnerMenu(): boolean {
      return Boolean(this.allowTransfer && this.owner?.isViewerPrimaryUser)
    },
    providerMeta(): ProviderMeta {
      return this.$providerMetaMap[this.vcsProvider ?? '']
    },
    joining(): string {
      return formatDate(this.dateJoined)
    }
  },
  methods: {
    getDefaultAvatar,
    updateRole(newRole: string): void {
      this.$emit('update-role', {
        id: this.id,
        email: this.email,
        fullName: this.fullName,
        role: this.role,
        newRole
      })
    },
    removeMember(): void {
      this.$emit('remove-member', {
        id: this.id,
        email: this.email,
        fullName: this.fullName
      })
    },
    transferOwnership(): void {
      this.$emit('transfer-ownership', {
        id: this.id,
        email: this.email,
        fullName: this.fullName
      })
    }
  }
})
